import React, { type PropsWithChildren } from 'react'

import { useTranslation } from 'react-i18next'

import { Render } from '@/shared/components/render-if'
import { cn } from '@/shared/lib'
import { formatToBRL } from '@/shared/utils/currency.utils'

interface ISimulacaoParcelasAgrupadasProps {
  isGrupoSaldo?: boolean
  parcelas: Array<{ valor: number; quantidade: number }>
  className?: string
}

const HeaderText = ({ children, className }: PropsWithChildren<{ className?: string }>) => {
  return (
    <header className={cn('space-x-2', className)}>
      <span className="font-poppins text-sm tracking-wide text-gray-500 md:text-base">{children}</span>
    </header>
  )
}

const SimulacaoParcelasAgrupadas: React.FC<ISimulacaoParcelasAgrupadasProps> = ({
  isGrupoSaldo: isConclusao,
  parcelas,
  className,
}) => {
  const { t } = useTranslation('simulador')
  if (isConclusao) {
    return <HeaderText className={className}>{t('simulador.grupos.pagamento_na_entrega')}</HeaderText>
  }

  const parcelasAgrupadasPorValor = Array.from(
    (parcelas ?? []).reduce((acc, item) => {
      const valor = item?.valor ?? 0
      acc.set(valor, (acc.get(valor) ?? 0) + (item?.quantidade ?? 0))
      return acc
    }, new Map<number, number>()),
    ([valor, quantidade]) => ({ valor, quantidade }),
  )

  return (
    <Render.If
      condition={parcelasAgrupadasPorValor.length > 0}
      fallback={<HeaderText className={className}>{t('simulador.grupos.nenhuma_parcela')}</HeaderText>}
    >
      {() =>
        parcelasAgrupadasPorValor.map((parcela) => (
          <div
            className={cn(
              'font-nunito flex flex-wrap items-center gap-2 text-sm break-words',
              'md:text-base',
              'lg:text-base lg:tracking-wide',
              className,
            )}
            key={parcela.valor}
          >
            <span className="text-gray-700">{parcela.quantidade}x</span>
            <span className="lg:break-words">{formatToBRL(parcela.valor)}</span>
          </div>
        ))
      }
    </Render.If>
  )
}

export { SimulacaoParcelasAgrupadas }
